import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Button, Spin, Alert } from "antd";
import axios from "api/axios";

const DOCUMENT_URL_PATTERN = /\/api\/events\/files/i;

export function isDpatDocumentUrl(href) {
  if (!href || typeof href !== "string") return false;
  try {
    return DOCUMENT_URL_PATTERN.test(href);
  } catch {
    return false;
  }
}

/** `/api/events/files?...` → `/events/files?...` for axios baseURL `https://dddp.gov.gh/api` */
export function toDhis2FileApiPath(href) {
  const url = new URL(href, "https://dddp.gov.gh");
  const path = url.pathname.replace(/^\/api/, "") || url.pathname;
  return `${path}${url.search}`;
}

async function fetchDocumentPreview(href) {
  const apiPath = toDhis2FileApiPath(href);
  const response = await axios.get(apiPath, { responseType: "blob" });
  const contentType = response.headers["content-type"] || "application/octet-stream";
  const blob = new Blob([response.data], { type: contentType });
  return {
    blobUrl: URL.createObjectURL(blob),
    contentType,
  };
}

const VIEWER_HEIGHT = "min(75vh, 800px)";

/**
 * Intercepts DPAT evidence links, loads files with DHIS2 basic auth via axios,
 * and previews them in a modal (blob URL avoids X-Frame-Options blocking).
 */
export default function DpatDocumentViewerProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [sourceUrl, setSourceUrl] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [contentType, setContentType] = useState("");
  const [title, setTitle] = useState("Document");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const blobUrlRef = useRef(null);

  const revokeBlobUrl = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  const closeViewer = useCallback(() => {
    setOpen(false);
    setSourceUrl("");
    setPreviewUrl("");
    setContentType("");
    setTitle("Document");
    setLoading(false);
    setError(null);
    revokeBlobUrl();
  }, [revokeBlobUrl]);

  const openViewer = useCallback(
    async (href, label) => {
      revokeBlobUrl();
      setSourceUrl(href);
      setTitle(label?.trim() || "Document");
      setPreviewUrl("");
      setContentType("");
      setError(null);
      setLoading(true);
      setOpen(true);

      try {
        const { blobUrl, contentType: type } = await fetchDocumentPreview(href);
        blobUrlRef.current = blobUrl;
        setPreviewUrl(blobUrl);
        setContentType(type);
      } catch (err) {
        console.error("DPAT document preview failed:", err);
        setError(
          err?.response?.status === 401
            ? "Could not load this document (authentication failed)."
            : "Could not load this document for preview. Use “Open in new tab” instead."
        );
      } finally {
        setLoading(false);
      }
    },
    [revokeBlobUrl]
  );

  useEffect(() => () => revokeBlobUrl(), [revokeBlobUrl]);

  const handleCaptureClick = useCallback(
    (event) => {
      const anchor = event.target.closest?.("a[href]");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!isDpatDocumentUrl(href)) return;
      event.preventDefault();
      event.stopPropagation();
      openViewer(href, anchor.textContent || anchor.getAttribute("title"));
    },
    [openViewer]
  );

  const isImage = contentType.startsWith("image/");

  return (
    <>
      <div onClickCapture={handleCaptureClick} className="dpat-document-viewer-root">
        {children}
      </div>
      <Modal
        title={title}
        open={open}
        onCancel={closeViewer}
        width={1100}
        style={{ maxWidth: "92vw" }}
        centered
        destroyOnClose
        footer={[
          <Button
            key="new-tab"
            href={sourceUrl || undefined}
            target="_blank"
            rel="noopener noreferrer"
            disabled={!sourceUrl}
          >
            Open in new tab
          </Button>,
          <Button key="close" type="primary" onClick={closeViewer}>
            Close
          </Button>,
        ]}
        styles={{
          body: { padding: loading || error ? 16 : 0, minHeight: VIEWER_HEIGHT },
        }}
      >
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3" style={{ height: VIEWER_HEIGHT }}>
            <Spin size="large" />
            <span className="text-sm text-muted-foreground">Loading document…</span>
          </div>
        ) : null}

        {!loading && error ? (
          <Alert type="warning" showIcon message={error} />
        ) : null}

        {!loading && !error && previewUrl ? (
          isImage ? (
            <div className="flex items-center justify-center overflow-auto p-4" style={{ maxHeight: VIEWER_HEIGHT }}>
              <img src={previewUrl} alt={title} className="max-h-full max-w-full object-contain" />
            </div>
          ) : (
            <iframe
              src={previewUrl}
              title={title}
              className="block w-full border-0"
              style={{ height: VIEWER_HEIGHT }}
            />
          )
        ) : null}
      </Modal>
    </>
  );
}
